import {
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import { authApi } from '@/lib/api/auth';
import { addWrapper, fromBase64, hasWrapper, removeWrapper, toBase64 } from '@/offline/crypto';
import { readMeta, writeMeta } from '@/offline/db';

/**
 * Biometrik perangkat (T-P7-05, FR-AUTH-06/07/08).
 *
 * Dua peran yang sering tertukar, dan di sini dipisahkan:
 *
 * 1. **Login biometrik** — menggantikan password saat online. Bekerja di semua
 *    authenticator platform.
 * 2. **Membuka kunci cache offline** — memerlukan ekstensi WebAuthn **PRF** untuk
 *    menurunkan kunci pembungkus. Tidak semua peramban punya PRF, jadi PIN tetap
 *    wajib didaftarkan: perangkat tanpa PRF masih bisa login dengan sidik jari,
 *    tetapi membuka cache-nya tetap lewat PIN. Menggantungkan cache pada PRF saja
 *    berarti collector terkunci dari datanya saat peramban diperbarui.
 */

const PRF_SALT_KEY = 'crypto.prfSalt';

export interface BiometricCapability {
  supported: boolean;
  platformAuthenticator: boolean;
}

export async function biometricCapability(): Promise<BiometricCapability> {
  if (!browserSupportsWebAuthn()) return { supported: false, platformAuthenticator: false };
  return { supported: true, platformAuthenticator: await platformAuthenticatorIsAvailable() };
}

/** Salt PRF tetap per perangkat; berubahnya salt berarti kunci turunan ikut berubah. */
async function prfSalt(): Promise<Uint8Array> {
  const stored = await readMeta<string>(PRF_SALT_KEY);
  if (stored) return fromBase64(stored);

  const salt = globalThis.crypto.getRandomValues(new Uint8Array(32));
  await writeMeta(PRF_SALT_KEY, toBase64(salt));
  return salt;
}

function base64url(bytes: Uint8Array): string {
  return toBase64(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

type ExtensionResults = { prf?: { results?: { first?: ArrayBuffer | Uint8Array } } };

function prfOutput(response: { clientExtensionResults?: unknown }): ArrayBuffer | null {
  const results = response.clientExtensionResults as ExtensionResults | undefined;
  const first = results?.prf?.results?.first;
  if (!first) return null;
  return first instanceof Uint8Array ? (first.buffer as ArrayBuffer) : first;
}

/**
 * Hasil PRF tidak boleh ikut ke server — itu bahan kunci enkripsi cache, dan
 * server tidak punya alasan apa pun untuk melihatnya.
 */
function withoutPrf<T extends { clientExtensionResults?: unknown }>(response: T): T {
  const { prf: _prf, ...rest } = (response.clientExtensionResults ?? {}) as Record<string, unknown>;
  return { ...response, clientExtensionResults: rest };
}

// --- pendaftaran ---------------------------------------------------------------

export interface EnableBiometricResult {
  registered: boolean;
  /** `true` bila cache offline kini dapat dibuka tanpa PIN. */
  unlocksCache: boolean;
}

/**
 * Daftarkan biometrik pada perangkat ini. PIN saat ini diminta karena pembungkus
 * baru dibuat dari data key yang sama — bukan dari kunci baru.
 */
export async function enableBiometric(
  deviceId: string,
  currentPin: string,
): Promise<EnableBiometricResult> {
  const salt = await prfSalt();
  const options = await authApi.webauthn.registerOptions(deviceId);

  const registration = await startRegistration({
    optionsJSON: {
      ...options,
      extensions: { ...(options.extensions as object), prf: { eval: { first: base64url(salt) } } },
    } as unknown as Parameters<typeof startRegistration>[0]['optionsJSON'],
  });

  await authApi.webauthn.registerVerify(deviceId, withoutPrf(registration));

  // Sebagian authenticator baru mengeluarkan PRF pada assertion, bukan saat
  // pendaftaran; satu assertion tambahan adalah cara paling andal mendapatkannya.
  const output = prfOutput(registration) ?? (await assertPrf(null));
  if (!output) return { registered: true, unlocksCache: false };

  const added = await addWrapper(
    { method: 'pin', pin: currentPin },
    { method: 'prf', output: output },
  );

  return { registered: true, unlocksCache: added };
}

export async function disableBiometric(deviceId: string): Promise<void> {
  await removeWrapper('prf');
  await authApi.revokeDevice(deviceId).catch(() => undefined);
}

export async function biometricUnlocksCache(): Promise<boolean> {
  return hasWrapper('prf');
}

// --- pemakaian -----------------------------------------------------------------

/**
 * Minta assertion lokal untuk mendapatkan PRF.
 *
 * `username === null` memakai kredensial yang sudah tersimpan di perangkat
 * (discoverable credential) — jalur offline, tanpa menyentuh server sama sekali.
 */
async function assertPrf(username: string | null): Promise<ArrayBuffer | null> {
  const salt = await prfSalt();

  const optionsJSON =
    username === null
      ? {
          challenge: base64url(globalThis.crypto.getRandomValues(new Uint8Array(32))),
          userVerification: 'required' as const,
          extensions: { prf: { eval: { first: base64url(salt) } } },
        }
      : {
          ...(await authApi.webauthn.loginOptions(username)),
          extensions: { prf: { eval: { first: base64url(salt) } } },
        };

  try {
    const assertion = await startAuthentication({
      optionsJSON: optionsJSON as unknown as Parameters<
        typeof startAuthentication
      >[0]['optionsJSON'],
    });
    return prfOutput(assertion);
  } catch {
    return null;
  }
}

/** Buka kunci cache lewat biometrik, tanpa jaringan (FR-AUTH-08). */
export async function unlockWithBiometric(): Promise<ArrayBuffer | null> {
  if (!(await hasWrapper('prf'))) return null;
  return assertPrf(null);
}

/** Login online dengan biometrik; mengembalikan sesi seperti login password. */
export async function loginWithBiometric(username: string) {
  const salt = await prfSalt();
  const options = await authApi.webauthn.loginOptions(username);

  const assertion = await startAuthentication({
    optionsJSON: {
      ...options,
      extensions: { ...(options.extensions as object), prf: { eval: { first: base64url(salt) } } },
    } as unknown as Parameters<typeof startAuthentication>[0]['optionsJSON'],
  });

  const session = await authApi.webauthn.loginVerify(username, withoutPrf(assertion));
  return { session, prf: prfOutput(assertion) };
}
